// Global (document-level) listeners must only be bound once no matter how
// many <filter-drawer> instances exist on the page - mirrors the guard used
// in cart-drawer.js for the same reason.
let globalFilterListenersBound = false;

function bindGlobalFilterListeners() {
  if (globalFilterListenersBound) return;
  globalFilterListenersBound = true;

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-filter-drawer-toggle]')) {
      event.preventDefault();
      document.dispatchEvent(new CustomEvent('filters:open'));
    }
  });
}

// Two native <input type="range"> elements stacked on top of each other
// (see the CSS trick in filter-drawer.liquid: transparent tracks, only the
// thumbs are pointer-interactive) driving the min/max number fields that
// actually carry the filter's `name`/param - the sliders themselves are
// nameless so they never end up in the submitted form data.
class PriceRange extends HTMLElement {
  connectedCallback() {
    this.minSlider = this.querySelector('[data-price-slider-min]');
    this.maxSlider = this.querySelector('[data-price-slider-max]');
    this.minInput = this.querySelector('[data-price-min-input]');
    this.maxInput = this.querySelector('[data-price-max-input]');
    this.rangeFill = this.querySelector('[data-price-slider-range]');
    this.rangeMax = Number(this.dataset.priceRangeMax) || 0;

    if (!this.minSlider || !this.maxSlider || !this.minInput || !this.maxInput) return;

    this.minSlider.addEventListener('input', () => this.onSliderInput('min'));
    this.maxSlider.addEventListener('input', () => this.onSliderInput('max'));
    this.minInput.addEventListener('input', () => this.onFieldInput('min'));
    this.maxInput.addEventListener('input', () => this.onFieldInput('max'));

    this.updateFill();
  }

  onSliderInput(which) {
    if (which === 'min') {
      if (Number(this.minSlider.value) > Number(this.maxSlider.value)) {
        this.minSlider.value = this.maxSlider.value;
      }
      this.minInput.value = this.minSlider.value;
    } else {
      if (Number(this.maxSlider.value) < Number(this.minSlider.value)) {
        this.maxSlider.value = this.minSlider.value;
      }
      this.maxInput.value = this.maxSlider.value;
    }
    this.updateFill();
  }

  onFieldInput(which) {
    if (which === 'min') {
      const value = this.minInput.value === '' ? 0 : Number(this.minInput.value);
      this.minSlider.value = String(Math.min(Math.max(value, 0), this.rangeMax));
    } else {
      const value = this.maxInput.value === '' ? this.rangeMax : Number(this.maxInput.value);
      this.maxSlider.value = String(Math.min(Math.max(value, 0), this.rangeMax));
    }
    this.updateFill();
  }

  updateFill() {
    if (!this.rangeMax) return;
    const min = Number(this.minSlider.value);
    const max = Number(this.maxSlider.value);
    const startPct = (min / this.rangeMax) * 100;
    const endPct = (max / this.rangeMax) * 100;
    if (this.rangeFill) {
      this.rangeFill.style.left = `${startPct}%`;
      this.rangeFill.style.width = `${Math.max(endPct - startPct, 0)}%`;
    }
    // Give whichever thumb is further along its track the higher z-index so
    // it stays grabbable once the two thumbs visually overlap.
    const minOnTop = min / this.rangeMax > 0.5;
    this.minSlider.classList.toggle('filter-drawer__price-slider-input--top', minOnTop);
    this.maxSlider.classList.toggle('filter-drawer__price-slider-input--top', !minOnTop);
  }
}

if (!customElements.get('price-range')) {
  customElements.define('price-range', PriceRange);
}

class FilterDrawer extends HTMLElement {
  connectedCallback() {
    bindGlobalFilterListeners();

    this.panel = this.querySelector('.filter-drawer__panel') || this;
    this.form = this.querySelector('[data-filter-drawer-form]');
    this.lastFocused = null;

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-filter-drawer-close]')) this.close();

      const groupToggle = event.target.closest('[data-filter-group-toggle]');
      if (groupToggle) this.toggleGroup(groupToggle);

      if (event.target.closest('[data-filter-drawer-clear]')) {
        event.preventDefault();
        this.clearFilters();
      }
    });

    this.addEventListener('change', (event) => {
      if (!this.form || !this.form.contains(event.target)) return;
      // Number fields are handled on 'input' below (debounced) - applying
      // them again here on 'change' would race the debounced call and can
      // fire a second, overlapping fetch mid-edit.
      if (!event.target.matches('input[type="checkbox"], select')) return;
      this.applyFilters();
    });

    this.addEventListener('input', (event) => {
      if (!this.form || !this.form.contains(event.target)) return;
      // The price-range sliders are nameless (they only drive the number
      // fields' values) so they never surface through the form's own
      // 'input' bubbling as `input[type="number"]` matches - listen for
      // both so dragging a thumb schedules an apply just like typing does.
      if (!event.target.matches('input[type="number"], input[type="range"]')) return;
      this.scheduleApply();
    });

    if (this.form) {
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.applyFilters().then(() => this.close());
      });
    }

    // Active-filter pills live outside the drawer (above the grid). They are
    // real links to Shopify's `url_to_remove`, so only intercept them when
    // they belong to this drawer's section - otherwise let them navigate.
    document.addEventListener('click', (event) => {
      const link = event.target.closest('[data-active-filter-remove]');
      if (!link) return;
      const container = link.closest('[data-active-filters]');
      if (!container || container.dataset.sectionId !== this.dataset.sectionId) return;
      event.preventDefault();
      const url = new URL(link.href, window.location.origin);
      this.fetchAndUpdate(url.searchParams);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.hidden) this.close();
    });

    document.addEventListener('filters:open', () => this.open());
  }

  open() {
    this.lastFocused = document.activeElement;
    this.hidden = false;
    document.body.style.overflow = 'hidden';
    this.panel.focus();
  }

  close() {
    this.hidden = true;
    document.body.style.overflow = '';
    if (this.lastFocused) this.lastFocused.focus();
  }

  toggleGroup(button) {
    const body = button.parentElement.querySelector('[data-filter-group-body]');
    const expanded = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', String(!expanded));
    if (body) body.hidden = expanded;
  }

  scheduleApply() {
    clearTimeout(this.applyDebounce);
    this.applyDebounce = setTimeout(() => this.applyFilters(), 600);
  }

  clearFilters() {
    if (!this.form) return;
    this.form.querySelectorAll('input[type="checkbox"]').forEach((el) => {
      el.checked = false;
    });
    this.form.querySelectorAll('input[type="number"]').forEach((el) => {
      el.value = '';
    });
    this.querySelectorAll('price-range').forEach((el) => {
      if (el.minSlider) el.minSlider.value = '0';
      if (el.maxSlider) el.maxSlider.value = String(el.rangeMax);
      el.updateFill();
    });
    this.applyFilters();
  }

  buildParams() {
    const params = new URLSearchParams(new FormData(this.form));
    Array.from(params.keys()).forEach((key) => {
      if (params.get(key) === '') params.delete(key);
    });
    return params;
  }

  applyFilters() {
    if (!this.form) return Promise.resolve();
    return this.fetchAndUpdate(this.buildParams());
  }

  // Fetches the section for the given filter/sort params and swaps the
  // results, count, active-filter pills and drawer contents in place. Falls
  // back to a full page load of the same URL if the request fails.
  async fetchAndUpdate(params) {
    if (!this.form) return;

    clearTimeout(this.applyDebounce);

    params.delete('section_id');
    params.delete('page');
    const search = params.toString();
    const pageUrl = search ? `${window.location.pathname}?${search}` : window.location.pathname;
    const sectionId = this.dataset.sectionId;
    const fetchUrl = `${window.location.pathname}?${search ? `${search}&` : ''}section_id=${sectionId}`;

    // A field the user is still actively editing (typically a price input)
    // must not be clobbered by the DOM swap below, and any older in-flight
    // request for this same drawer is now stale - abort it so an
    // out-of-order response can't overwrite what the user has typed since.
    const active = document.activeElement;
    const activeName = active && this.form.contains(active) ? active.name : null;

    if (this.pendingController) this.pendingController.abort();
    const controller = new AbortController();
    this.pendingController = controller;

    this.classList.add('loading');
    try {
      const response = await fetch(fetchUrl, { signal: controller.signal });
      if (!response.ok) throw new Error(`Filter request failed: ${response.status}`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      this.updateFromDocument(doc, activeName);
      window.history.pushState({}, '', pageUrl);
    } catch (error) {
      if (error.name === 'AbortError') return;
      window.location.href = pageUrl;
    } finally {
      if (this.pendingController === controller) {
        this.pendingController = null;
        this.classList.remove('loading');
      }
    }
  }

  updateFromDocument(doc, activeName) {
    ['[data-collection-count]', '[data-collection-results]'].forEach((selector) => {
      const current = document.querySelector(selector);
      const next = doc.querySelector(selector);
      if (current && next) current.outerHTML = next.outerHTML;
    });

    // Optional: only present when the section renders the active-filters snippet.
    const pillsSelector = `[data-active-filters][data-section-id="${CSS.escape(this.dataset.sectionId || '')}"]`;
    const currentPills = document.querySelector(pillsSelector);
    const nextPills = doc.querySelector(pillsSelector);
    if (currentPills && nextPills) currentPills.outerHTML = nextPills.outerHTML;

    const currentRefresh = this.querySelector('[data-filter-drawer-refresh]');
    const nextRefresh = doc.querySelector('[data-filter-drawer-refresh]');
    if (currentRefresh && nextRefresh) {
      currentRefresh.outerHTML = nextRefresh.outerHTML;

      // The swap above just destroyed and recreated every field in the
      // drawer, including whichever one was focused - reattach focus to its
      // replacement (matched by name) so typing can continue uninterrupted.
      if (activeName) {
        const toFocus = this.form.querySelector(`[name="${CSS.escape(activeName)}"]`);
        if (toFocus) {
          const value = toFocus.value;
          toFocus.focus();
          // Number inputs don't support setSelectionRange; re-assigning the
          // value after focusing moves the caret to the end instead of
          // leaving it at position 0.
          toFocus.value = '';
          toFocus.value = value;
        }
      }
    }

    document.dispatchEvent(new CustomEvent('filters:applied'));
  }
}

if (!customElements.get('filter-drawer')) {
  customElements.define('filter-drawer', FilterDrawer);
}
